import { useContext } from 'react';

import { db } from '../../firebase/config';
import { SavedIcon } from '../../StateManagment/SavedIcon';
import {
    AGENDA_COLOR_OPTIONS,
    SELECTABLE_AGENDA_COLORS,
} from '../../helpers/calendar/personaAgendaColors';

// AI-CONTEXT: See docs/features/agenda-persoonskleur-en-huisbezoek-titel.md

/**
 * Kiest de eigen agendakleur van een persona: de afspraken waarop alléén deze
 * persoon is ingepland krijgen die kleur. Zijn roosterblokken niet — die zijn
 * altijd grijs, want dat is tijd waarop nog niets gepland staat.
 *
 * Een vaste rij knoppen, geen vrije kleurkiezer — de lijst in
 * `personaAgendaColors.js` is getoetst op leesbare tekst. Grijs en paars staan
 * er niet bij: die betekenen in de agenda niet-ingeplande tijd en een afspraak
 * met meer dan één persoon. Wie er al een had houdt hem, en dan staat hij hier
 * wél tussen — anders zou de rij zeggen dat deze persona 'Standaard' is.
 *
 * @param {object} props
 * @param {string} props.docid - Het `Personas`-document dat de keuze krijgt
 * @param {string} [props.value] - De opgeslagen sleutel (`Personas.AgendaColor`); leeg = standaardkleur
 * @param {boolean} props.canEdit - Zonder bewerkrecht is de rij alleen te bekijken
 * @returns {JSX.Element}
 */
const AgendaColorPicker = ({ docid, value, canEdit }) => {
    const [, setSaved] = useContext(SavedIcon);
    const current = typeof value === 'string' ? value : '';

    // De aan te bieden kleuren, plus de opgeslagen kleur als die inmiddels
    // gereserveerd is. `AGENDA_COLOR_OPTIONS` kent die nog, dus er hoeft niets
    // uit een tweede lijst te komen. Filteren op die volledige lijst in plaats
    // van de gereserveerde kleur achteraan te plakken: anders verspringt
    // 'paars' naar achter 'bruin' en verandert de rij van plek onder iemands
    // handen zodra hij hem één keer wijzigt.
    const options = AGENDA_COLOR_OPTIONS.filter(
        (o) => o.key === current || SELECTABLE_AGENDA_COLORS.some((k) => k.key === o.key)
    );

    /** @param {string} key */
    const choose = (key) => {
        db.collection('Personas').doc(docid).update({ AgendaColor: key })
            .then(() => setSaved('flex'));
    };

    return (
        <div className="agenda-color-picker">
            <p className="agenda-color-picker__label" id={`agenda-color-label-${docid}`}>Kleur in de agenda</p>
            <div
                className="agenda-color-picker__options"
                role="radiogroup"
                aria-labelledby={`agenda-color-label-${docid}`}
            >
                <button
                    type="button"
                    role="radio"
                    aria-checked={current === ''}
                    aria-label="Standaard"
                    title="Standaard: de kleur van het soort afspraak"
                    className="agenda-color-picker__swatch agenda-color-picker__swatch--none"
                    disabled={!canEdit}
                    onClick={() => choose('')}
                >
                    Standaard
                </button>
                {options.map((option) => (
                    <button
                        key={option.key}
                        type="button"
                        role="radio"
                        aria-checked={current === option.key}
                        aria-label={option.label}
                        title={option.label}
                        className="agenda-color-picker__swatch"
                        // Geen opmaak maar een gegeven: de kleur zelf komt uit de
                        // vaste lijst, de CSS leest hem als variabele — zelfde
                        // aanpak als `--role-dot` op het rolbolletje in de agenda.
                        style={{ '--agenda-swatch': option.color, '--agenda-swatch-bg': option.backgroundColor }}
                        disabled={!canEdit}
                        onClick={() => choose(option.key)}
                    />
                ))}
            </div>
        </div>
    );
};

export default AgendaColorPicker;
